/** Known-safe scalars. Nested objects on Errors (e.g. gateway BYOK maps) are dropped. */
const ALLOWED_FIELDS = ['name', 'message', 'code', 'status', 'statusCode', 'stack', 'details', 'hint', 'digest', 'isRetryable'] as const;

const MAX_FIELD_CHARS = 1000;
const MAX_CAUSE_DEPTH = 3;

/** Brands a sanitized record so the logger does not duck-type it as an Error and re-extract. */
const SANITIZED_ERROR_LOG = Symbol('sanitizedErrorForLogging');

export type SanitizedErrorLogValue = string | number | boolean | SanitizedErrorLog;

export type SanitizedErrorLog = {
  [key: string]: SanitizedErrorLogValue;
};

export const isSanitizedErrorLog = (value: unknown): value is SanitizedErrorLog =>
  typeof value === 'object' && value !== null && SANITIZED_ERROR_LOG in value;

const truncate = (value: string): string => (value.length > MAX_FIELD_CHARS ? `${value.slice(0, MAX_FIELD_CHARS)}...` : value);

const brand = (sanitized: SanitizedErrorLog): SanitizedErrorLog => {
  Object.defineProperty(sanitized, SANITIZED_ERROR_LOG, { value: true });
  return sanitized;
};

const countDroppedKeys = (source: object, sanitized: SanitizedErrorLog): number => {
  const included = new Set(Object.keys(sanitized));
  let dropped = 0;
  for (const key of Object.getOwnPropertyNames(source)) {
    if (!included.has(key)) {
      dropped += 1;
    }
  }
  return dropped;
};

/**
 * Allowlisted-scalars view of an unknown error, safe to hand to the logger.
 *
 * AI SDK / Vercel AI Gateway Errors can carry the request that produced them, including
 * `providerOptions.gateway.byok` (every configured provider credential). The logger used to
 * copy all own-properties recursively — so a raw `logger.error(..., err)` wrote credentials
 * to production logs.
 *
 * String/number/boolean allowlisted fields survive, plus a bounded sanitized `cause` chain
 * and `droppedKeyCount` when anything was stripped. Nested objects never pass.
 *
 * Prefer relying on automatic use inside `Logger._extractErrorProperties`. Exporting this
 * helper is for rare non-Error bags; it is not a substitute for the automatic path.
 */
export const sanitizeErrorForLogging = (error: unknown, causeDepth = 0): SanitizedErrorLog => {
  if (isSanitizedErrorLog(error)) {
    return error;
  }

  if (error === null || typeof error !== 'object') {
    return brand({ value: truncate(String(error)) });
  }

  const source = error as Record<string, unknown>;
  const sanitized: SanitizedErrorLog = {};

  for (const field of ALLOWED_FIELDS) {
    const value = source[field];
    if (typeof value === 'string') {
      sanitized[field] = truncate(value);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[field] = value;
    }
  }

  if (source.cause !== undefined && source.cause !== null) {
    if (causeDepth >= MAX_CAUSE_DEPTH) {
      sanitized.cause = '[max-depth]';
    } else {
      sanitized.cause = sanitizeErrorForLogging(source.cause, causeDepth + 1);
    }
  }

  const droppedKeyCount = countDroppedKeys(source, sanitized);
  if (droppedKeyCount > 0) {
    sanitized.droppedKeyCount = droppedKeyCount;
  }

  return brand(sanitized);
};
